/**
 * How the valuation gets from finished statements to a number.
 *
 * These were in `accounting/formulas.ts`, which is where every computed line used to
 * live. They moved when the disciplines split: a discounted cash flow is not an
 * accounting formula, and an accounting user opening the formulas panel has no reason
 * to be shown one. The accounting panel now holds only what shapes the statements.
 *
 * Same shape as the accounting list, so one panel renders either.
 */

import type { FormulaSection } from '../formulaTypes';

export const VALUATION_FORMULAS: FormulaSection[] = [

    {
      section: 'Discounted cash flow',
      entries: [
        {
          label: 'Free cash flow to firm',
          formula: 'EBITDA − Capex − Change in working capital − Cash tax',
          sources: ['pnl', 'fixed-assets', 'balance-sheet'],
        },
        {
          label: 'Cash tax',
          formula: 'Tax charge + the movement in the tax creditor',
          sources: ['pnl', 'control'],
          note: 'The charge, adjusted for what was not yet paid — not the creditor movement on its own.',
        },
        {
          label: 'Change in working capital',
          formula:
            '(Stocks + Trade & Other Debtors + VAT) − (Trade & Other Creditors), this year less last',
          sources: ['balance-sheet'],
          note: 'The first forecast year is compared against the last HISTORICAL year, so the first year of growth is not free.',
        },
        {
          label: 'Discount factor',
          formula: '1 ÷ (1 + WACC)^n',
          note: 'n counts forecast years only. Historical years are not discounted — they have already happened.',
        },
        {
          label: 'Terminal value',
          formula: 'Final year FCFF × (1 + g) ÷ (WACC − g)',
        },
        {
          label: 'DCF enterprise value',
          formula: 'Σ present values + present value of the terminal value',
        },
      ],
    },
    {
      section: 'Multiples and the blend',
      entries: [
        {
          label: 'Multiples enterprise value',
          formula: 'Terminal year EBITDA × exit multiple × discount factor',
          sources: ['pnl'],
          note: 'Discounted, because an EXIT multiple values the business on the day it is sold. Undiscounted it would be a future number averaged against a present one.',
        },
        {
          label: 'Blended enterprise value',
          formula: 'The average of the methods that produced a figure',
          note: 'A method that produced nothing is excluded rather than averaged in as zero.',
        },
        {
          label: 'Equity value',
          formula: 'Enterprise value − net debt',
          sources: ['balance-sheet'],
        },
      ],
    },
];
